import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Bookmark, 
  BookmarkCheck, 
  ExternalLink, 
  Zap, 
  MapPin, 
  Building2, 
  DollarSign,
  Clock
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface JobCardProps {
  job: {
    id: string;
    title: string;
    company: string;
    location?: string;
    salary?: string;
    description?: string;
    job_url: string;
    posted_at?: string;
    posted_date?: string;
    source?: 'jsearch' | 'apify_linkedin';
    apply_type?: 'EASY_APPLY' | 'EXTERNAL';
    saved?: boolean;
    job_type?: string;
    remote?: string;
    logo?: string;
  };
  onSmartApply: (job: JobCardProps['job']) => void;
  onSave?: (job: JobCardProps['job']) => void;
  onClick?: (job: JobCardProps['job']) => void;
  showSaveButton?: boolean;
}

export function JobCard({ job, onSmartApply, onSave, onClick, showSaveButton = true }: JobCardProps) {
  const isEasyApply = job.apply_type === 'EASY_APPLY';
  const isLinkedIn = job.source === 'apify_linkedin';
  
  const formatPostedDate = (dateString?: string) => {
    if (!dateString) return 'Recently';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  return (
    <Card 
      className="p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 cursor-pointer"
      onClick={(e) => {
        // Prevent card click when clicking buttons
        if ((e.target as HTMLElement).closest('button, a')) return;
        onClick?.(job);
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-semibold truncate">{job.title}</h3>
              {isEasyApply && (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                  Easy Apply
                </Badge>
              )}
              {isLinkedIn && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                  LinkedIn
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                <span className="font-medium">{job.company}</span>
              </div>
              
              {job.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{job.location}</span>
                </div>
              )}
              
              {job.salary && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  <span>{job.salary}</span>
                </div>
              )}
            </div>
          </div>

          {job.logo && (
            <img 
              src={job.logo} 
              alt={`${job.company} logo`}
              className="h-12 w-12 rounded-lg object-contain bg-muted p-1"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>

        {/* Description Preview */}
        {job.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {job.description}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{formatPostedDate(job.posted_at || job.posted_date)}</span>
            </div>
            
            {job.job_type && (
              <Badge variant="secondary" className="text-xs">
                {job.job_type}
              </Badge>
            )}
            
            {job.remote && (
              <Badge variant="secondary" className="text-xs">
                {job.remote}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {showSaveButton && onSave && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSave(job);
                }}
                className="gap-1"
              >
                {job.saved ? (
                  <>
                    <BookmarkCheck className="h-4 w-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <Bookmark className="h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              asChild
              className="gap-1"
            >
              <a 
                href={job.job_url} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
                View
              </a>
            </Button>

            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onSmartApply(job);
              }}
              className="gap-1"
            >
              <Zap className="h-4 w-4" />
              Smart Apply
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
